import React, { useEffect, useRef } from "react";
import Navbar from "../../Components/Navbar";
import Images from "../../Images";
import {
  Bg,
  BoxContainer,
  Underline,
} from "../../Components/Styles/PageStyles";
import tw from "tailwind-styled-components";
import * as Yup from "yup";
import axios from "axios";
import Config from "../../Config";
import Loading from "../../Components/Loading";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import { useFormik } from "formik";
import {
  InputGroup,
  Label,
  SubmitBtn,
} from "../../Components/Styles/InputStyles";
import { CLIENT_CONFIG_MAP } from "../../Config";
import { useSelector } from "react-redux";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { useMutation } from "react-query";
import { useTranslation } from "react-i18next";
import MyEditor from "../../Components/Text";


const validationSchema = Yup.object().shape({
  provider: Yup.number().required("Please Select Provider"),
  type: Yup.number().required("Please Select Type"),
});

const EditClientConfigPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const user = useSelector((state) => state.UserReducer.user);
  const { t } = useTranslation();

  const selectedData = location.state;

  useEffect(() => {
    if (!selectedData) navigate(-1);
  }, [selectedData, navigate]);

  const EditClientConfigFunction = async (values) => {
    const body = new FormData();
    body.append("id", selectedData.id);
    body.append("clientId", id);
    body.append("provider", values.provider);
    body.append("type", values.type);
    body.append("value", values.value);

    return axios.post(`${Config.apiUrl}/editclientconfig`, body, {
      headers: {
        authorization: "Bearer " + user.token,
        "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8",
      },
    });
  };

  const { isLoading: editLoading, mutate: editClientMutate } = useMutation(
    EditClientConfigFunction,
    {
      onSuccess: (data) => {
        if (data?.data?.status === "OK") {
          toast.success(data?.data?.msg || "Updated Successfully");
          navigate(-1);
        } else {
          toast.error(data?.data?.msg || "Error");
        }
      },
      onError: (error) => {
        toast.error(error?.response?.data?.msg || "Error");
      },
    }
  );

  const formik = useFormik({
    initialValues: {
      provider: selectedData?.provider || "",
      type: selectedData?.type || "",
      value: selectedData?.value || "",
    },
    enableReinitialize: true,
    validationSchema,
    onSubmit: (values) => {
      editClientMutate(values);
    },
  });

  const provider = formik.values.provider;



  const typeOptions = CLIENT_CONFIG_MAP[provider]
    ? Object.entries(CLIENT_CONFIG_MAP[provider].types).map(
        ([value, obj]) => ({
          value,
          label: obj.label,
        })
      )
    : [];

  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) return;

    formik.setFieldValue("type", "");
    formik.setFieldValue("value", "");
  }, [provider]);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    formik.setFieldValue("value", "");
  }, [formik.values.type]);

  const isRichText =
    CLIENT_CONFIG_MAP[provider]?.types[formik.values.type]?.richText;

  const isBooleanType =
    CLIENT_CONFIG_MAP[provider]?.types[formik.values.type]?.boolean;
  
  const isTransactionType =
    CLIENT_CONFIG_MAP[provider]?.types[formik.values.type]?.transaction;
  
  const isAttendanceType =
    CLIENT_CONFIG_MAP[provider]?.types[formik.values.type]?.attendance;   

  return (
    <Bg
      style={{
        backgroundImage: `url(${Images.BeamsBg})`,
        backgroundRepeat: "no-repeat",
        backgroundSize: "contain",
        backgroundPosition: "top left",
        height: "100vh",
      }}
    >
      <Navbar />

      <BoxContainer>
        <Title>
          {t("Edit Client Config")}
          <span> /{selectedData?.name}</span>
        </Title>

        <Underline />

        <form onSubmit={formik.handleSubmit}>
          <FormWrapper>
            {/* Provider + Type */}
            <CombineInputGroup>
              <InputGroup>
                <Label>Provider</Label>
                <Select
                  className="w-full pl-2 rounded-lg"
                  name="provider"
                  value={formik.values.provider}
                  onChange={formik.handleChange}
                  displayEmpty
                >
                  <MenuItem value="" disabled>
                    Select Provider
                  </MenuItem>

                  {Object.entries(CLIENT_CONFIG_MAP).map(([key, val]) => (
                    <MenuItem key={key} value={key}>
                      {val.providerLabel}
                    </MenuItem>
                  ))}
                </Select>
                {formik.touched.provider && formik.errors.provider && (
                  <ErrorText>{formik.errors.provider}</ErrorText>
                )}
              </InputGroup>

              <InputGroup> 
                <Label>Type</Label>
                <Select
                  className="w-full pl-2 rounded-lg"
                  name="type"
                  value={formik.values.type}
                  onChange={formik.handleChange}
                  displayEmpty
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: 340,
                        overflowY: "auto",
                      },
                    },
                  }}
                >
                  <MenuItem value="" disabled>
                    Select Type
                  </MenuItem>
                  {typeOptions.map((item) => (
                    <MenuItem key={item.value} value={item.value}>
                      {item.label}
                    </MenuItem>
                  ))}
                </Select>
                {formik.touched.type && formik.errors.type && (
                  <ErrorText>{formik.errors.type}</ErrorText>
                )}
              </InputGroup>
            </CombineInputGroup>

            <CombineInputGroup>
              <InputGroup className="w-full">
                <Label>Value</Label>
                {isRichText ? (
                  <div className="border border-gray-300 rounded-md overflow-hidden">
                    <MyEditor
                      value={formik.values.value}
                      onChange={(content) =>
                        formik.setFieldValue("value", content)
                      }
                    />
                  </div>
                ) : isBooleanType ? (
                  <Select
                    className="w-full pl-2 rounded-lg"
                    name="value"
                    value={formik.values.value}
                    onChange={formik.handleChange}
                    displayEmpty
                  >
                    <MenuItem value="" disabled>
                      Select Option
                    </MenuItem>
                    <MenuItem value="0">DISABLE</MenuItem>
                    <MenuItem value="1">ENABLE</MenuItem>
                  </Select>
                ) : isAttendanceType ? (

                  <Select
                    className="w-full pl-2 rounded-lg"
                    name="value"
                    value={formik.values.value}
                    onChange={formik.handleChange}
                    displayEmpty
                  >
                    <MenuItem value="" disabled>
                      Select Attendance
                    </MenuItem>
                    <MenuItem value="0">DISABLE</MenuItem>
                    <MenuItem value="1">PRESENT</MenuItem>
                    <MenuItem value="2">ABSENT</MenuItem>
                    <MenuItem value="3">BOTH ABSENT AND PRESENT</MenuItem>
                  </Select>

                )  : isTransactionType ? (
                  <Select
                    className="w-full pl-2 rounded-lg"
                    name="value"
                    value={formik.values.value}
                    onChange={formik.handleChange}
                    displayEmpty
                  >
                    <MenuItem value="" disabled>
                      Select Option
                    </MenuItem>
                    <MenuItem value="1">FLAT</MenuItem>
                    <MenuItem value="2">PERCENTAGE</MenuItem>
                  </Select>
                ) : (
                  <Input
                    name="value"
                    value={formik.values.value}
                    onChange={formik.handleChange}
                  />
                )}
              </InputGroup>
            </CombineInputGroup>
          </FormWrapper>

          <BtnWrapper>
            <SubmitBtn type="submit">
              {editLoading ? (
                <Loading height={20} width={20} noPadding />
              ) : (
                "Update Client"
              )}
            </SubmitBtn>
          </BtnWrapper>
        </form>
      </BoxContainer>
    </Bg>
  );
};

const Title = tw.h1`text-xl text-gray-800 font-medium`;
const Input = tw.input`truncate border border-gray-300 rounded pl-2 focus:ring-1 focus:ring-sky-600 focus:border-sky-600`;
const ErrorText = tw.div`text-red-500 text-sm`;
const FormWrapper = tw.div`px-1 py-4 space-y-3`;
const CombineInputGroup = tw.div`flex space-x-7`;
const BtnWrapper = tw.div`w-full flex justify-center mt-8`;

export default EditClientConfigPage;
